import { memo, useState, useRef, useEffect } from 'react';
import { useModal } from '../../contexts/ModalContext';
import { useAuth } from '../../contexts/AuthContext';
import { getCornellClassesUrl } from '../../utils/courseUtils';
import { getSemesterName } from '../../utils/semesterUtils';
import StarButton from '../icons/StarButton';
import CalendarCheck from '../icons/CalendarCheck';
import EyeIcon from '../icons/EyeIcon';
import { getTruncatedText } from '../../utils/textUtils';
import { CourseLite, CourseOption, CourseSummary } from '../../types/course.response';
import { getCourseKey } from '../../utils/courseUtils';
import CourseRowDetail from './CourseRowDetail';
import { CourseRowDetailSkeleton } from '../loading/CourseRowSkeleton';
import UnappliedWarning from '../shared/UnappliedWarning';
import SchedulePreviewModal from '../shared/SchedulePreviewModal';

interface CourseRowProps {
    /** 折叠行始终用摘要渲染(切片到达即可见) */
    course: CourseSummary;
    /** 完整展示数据,补水(/courses/batch)到位后才有;驱动展开详情 */
    hydrated?: CourseOption;
    selectedSemester: string;
    onAddToSchedule: (course: CourseOption, classSectionIds?: number[]) => void | Promise<void>;
    onMarkAsTaken: (course: CourseOption) => void | Promise<void>;
    onDeleteFromSchedule: (course: CourseOption) => void | Promise<void>;
    onFavoriteToggle: (course: CourseLite) => void | Promise<void>;
    optimisticFavorites: Map<string, boolean>;
    userCourses?: CourseOption[];
    requirementId?: string;
    /** GROUP 槽位:本组可选课数,与 onShowOptions 一起在尾部展示 "👁 N options" */
    groupOptionCount?: number;
    /** GROUP 槽位(仅 section):点击尾部眼睛,打开本组全部课程弹窗 */
    onShowOptions?: () => void;
}

function CourseRow({
    course,
    hydrated,
    selectedSemester,
    onAddToSchedule,
    onMarkAsTaken,
    onDeleteFromSchedule,
    onFavoriteToggle,
    optimisticFavorites,
    userCourses,
    requirementId,
    groupOptionCount,
    onShowOptions,
}: CourseRowProps) {
    const showOptionsControl = onShowOptions != null && groupOptionCount != null && groupOptionCount > 1;
    const { openLoginModal } = useModal();
    const { isLoggedIn } = useAuth();
    const [showStatusTooltip, setShowStatusTooltip] = useState<boolean>(false);
    const [showStarTooltip, setShowStarTooltip] = useState<boolean>(false);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    const starTooltipTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

    const containerRef = useRef<HTMLDivElement>(null);
    const [truncatedText, setTruncatedText] = useState<string>('');
    const [isPending, setIsPending] = useState<boolean>(false);
    const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);

    // Calculate when container is mounted and row is expanded (展开 + 补水到位后才有描述)
    useEffect(() => {
        if (isExpanded && hydrated && containerRef.current && hydrated.courseInfo.description && !truncatedText) {
            const width = containerRef.current.offsetWidth;
            const truncated = getTruncatedText(hydrated.courseInfo.description, width);
            setTruncatedText(truncated);
        }
    }, [isExpanded, hydrated, truncatedText]);

    // Filter enroll groups for the selected semester(摘要无 enrollGroups,补水后才有)
    const filteredEnrollGroups = hydrated?.enrollGroups.filter(
        eg => eg.semester === selectedSemester
    ) ?? [];

    useEffect(() => {
        return () => {
            if (starTooltipTimeoutRef.current) {
                clearTimeout(starTooltipTimeoutRef.current);
            }
        };
    }, []);

    const handleStarTooltipShow = () => {
        starTooltipTimeoutRef.current = setTimeout(() => {
            setShowStarTooltip(true);
        }, 500); // 500ms delay
    };

    const handleStarTooltipHide = () => {
        if (starTooltipTimeoutRef.current) {
            clearTimeout(starTooltipTimeoutRef.current);
        }
        setShowStarTooltip(false);
    };

    const handleFavoriteClick = async () => {
        if (!isLoggedIn) {
            openLoginModal();
            return;
        }
        
        await onFavoriteToggle(course);
    };

    const handleRowClick = () => {
        setIsExpanded(!isExpanded);
    };

    const handleAction = async (action: (courseOption: CourseOption) => void | Promise<void>) => {
        if (!hydrated) return; // 详情动作仅在补水到位的展开态触发
        setIsPending(true);
        try {
            await action(hydrated);
        } finally {
            setIsPending(false);
        }
    };

    const handleOpenPreview = () => {
        if (!isLoggedIn) {
            openLoginModal();
            return;
        }
        setShowPreviewModal(true);
    };

    const handlePreviewConfirm = async (classSectionIds: number[]) => {
        if (!hydrated) return;
        setIsPending(true);
        try {
            await onAddToSchedule(hydrated, classSectionIds);
        } finally {
            setIsPending(false);
            setShowPreviewModal(false);
        }
    };

    const cornellClassesUrl = hydrated ? getCornellClassesUrl(hydrated) : null;
    const isOnSchedule = course.userState.status && ['COMPLETED', 'IN_PROGRESS', 'PLANNED'].includes(course.userState.status);

    // Check optimistic favorites map first, then fall back to real state
    const displayIsFavorite = (() => {
        const courseKey = getCourseKey(course);
        if (optimisticFavorites.has(courseKey)) {
            return optimisticFavorites.get(courseKey)!;
        }
        return course.userState.status === 'SAVED';
    })();

    const availabilityText = () => {
        if (!course.courseState.isSemesterAvailable) {
            return (
                <>
                    Not offered in {getSemesterName(selectedSemester)}{' '}
                    {course.courseInfo.lastOfferedSemester && <span className="text-gray-500">
                        (Last offered {getSemesterName(course.courseInfo.lastOfferedSemester)})
                    </span>}
                </>
            );
        }
        if (!course.courseState.isLocationAvailable) {
            return 'The course is not offered in Ithaca.';
        }
        return null;
    }

    const isScheduled = course.userState.isScheduled;

    const unappliedBinding = requirementId
        ? course.userState.requirementBindings.find(
              b => b.requirementId === requirementId &&
                   (b.status === 'UNAPPLIED' || b.status === 'FORCE_UNAPPLIED')
          )
        : undefined;

    return (
        <>
            <tr 
                className="relative group cursor-pointer"
                onClick={handleRowClick}
            >
                <td className={`w-10 pr-2 pt-4 ${isExpanded ? 'pb-2' : 'border-b pb-4'}`}>
                    <div className="flex items-center justify-center">
                        {isOnSchedule ? (
                            <div
                                className="relative w-6 h-6 flex items-center justify-center"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <CalendarCheck
                                    className="w-6 h-6 cursor-pointer"
                                    color={
                                        course.userState.status === 'COMPLETED' ? '#15803d' :
                                        course.userState.status === 'IN_PROGRESS' ? '#f59e0b' :
                                        '#1d4ed8'
                                    }
                                    onMouseEnter={() => setShowStatusTooltip(true)}
                                    onMouseLeave={() => setShowStatusTooltip(false)}
                                />
                                {showStatusTooltip && (
                                    <div className="absolute top-full left-0 mt-1 px-2 py-1 bg-white rounded shadow-lg border border-gray-200 whitespace-nowrap z-10">
                                        <div className={
                                            course.userState.status === 'COMPLETED' ? 'text-green-700' :
                                            course.userState.status === 'IN_PROGRESS' ? 'text-amber-700' :
                                            'text-blue-700'
                                        }>
                                            <span className="font-medium">
                                                {course.userState.status === 'COMPLETED' ? 'Taken' :
                                                 course.userState.status === 'IN_PROGRESS' ? 'Current' :
                                                 'Planned'}
                                            </span>
                                            <span className={`text-sm block ${
                                                course.userState.status === 'COMPLETED' ? 'text-green-600/80' :
                                                course.userState.status === 'IN_PROGRESS' ? 'text-amber-600/80' :
                                                'text-blue-600/80'
                                            }`}>
                                                {isScheduled && getSemesterName(course.userState.semester ?? undefined)}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : course.type !== 'EXAM' ? (
                            <div
                                className="w-6 h-6 flex items-center justify-center relative"
                                onMouseEnter={handleStarTooltipShow}
                                onMouseLeave={handleStarTooltipHide}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <StarButton
                                    isFavorite={displayIsFavorite}
                                    onClick={handleFavoriteClick}
                                />
                                {showStarTooltip && (
                                    <div className="absolute top-full left-0 mt-1 px-2 py-1 bg-gray-700 rounded shadow-lg whitespace-nowrap z-10">
                                        <span className="text-sm text-white">
                                            {displayIsFavorite ? 'Unfavorite' : 'Favorite'}
                                        </span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="w-6 h-6" />
                        )}
                    </div>
                </td>
            <td className={`md:w-32 pl-2 pr-2 pt-4 ${isExpanded ? 'pb-2 font-bold' : 'border-b pb-4'} ${
                !course.courseState.isSemesterAvailable || !course.courseState.isLocationAvailable ? 'text-gray-400' : ''
            }`}>
                <div className="whitespace-nowrap text-sm md:text-base md:text-inherit">
                    {course.courseInfo.subject} {course.courseInfo.number}
                </div>
                <div className="md:hidden text-sm mt-0.5">
                    <div className="flex items-start gap-1.5 whitespace-normal">
                        <span className={!course.courseState.isSemesterAvailable || !course.courseState.isLocationAvailable ? '' : 'text-gray-900'}>
                            {course.topic ? `${course.courseInfo.titleLong} - ${course.topic}` : course.courseInfo.titleLong}
                        </span>
                        {unappliedBinding && (
                            <UnappliedWarning binding={unappliedBinding} className="mt-0.5" />
                        )}
                    </div>
                </div>
            </td>
            <td className={`md:w-[32rem] pl-2 pr-4 pt-4 ${isExpanded ? 'pb-2 font-bold' : 'border-b pb-4'} break-words relative ${
                !course.courseState.isSemesterAvailable || !course.courseState.isLocationAvailable ? 'text-gray-400' : ''
            }`}>
                <div className="hidden md:flex items-start gap-1.5">
                    <span>
                        {course.topic ? `${course.courseInfo.titleLong} - ${course.topic}` : course.courseInfo.titleLong}
                    </span>
                    {unappliedBinding && (
                        <UnappliedWarning binding={unappliedBinding} className="mt-0.5" />
                    )}
                </div>
            </td>
            <td className={`px-2 pt-4 ${isExpanded ? 'pb-2' : 'border-b pb-4'}`}>
                <div className="flex items-center justify-end">
                    {showOptionsControl && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onShowOptions!(); }}
                            className="flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-gray-700 transition-colors whitespace-nowrap"
                            aria-label={`View all ${groupOptionCount} options in this group`}
                        >
                            <EyeIcon className="w-4 h-4" />
                            <span>{groupOptionCount} options</span>
                        </button>
                    )}
                </div>
            </td>
        </tr>
        {isExpanded && (
            hydrated ? (
                <CourseRowDetail
                    courseOption={hydrated}
                    selectedSemester={selectedSemester}
                    cornellClassesUrl={cornellClassesUrl}
                    truncatedText={truncatedText}
                    containerRef={containerRef}
                    availabilityText={availabilityText()}
                    enrollGroups={filteredEnrollGroups}
                    isPending={isPending}
                    onMarkAsTaken={() => handleAction(onMarkAsTaken)}
                    onAddToSchedule={handleOpenPreview}
                    onDeleteFromSchedule={() => handleAction(onDeleteFromSchedule)}
                    userCourses={userCourses}
                />
            ) : (
                <CourseRowDetailSkeleton />
            )
        )}
        {showPreviewModal && hydrated && userCourses && (
            <SchedulePreviewModal
                courseOption={hydrated}
                semester={selectedSemester}
                userCourses={userCourses}
                onConfirm={handlePreviewConfirm}
                onCancel={() => setShowPreviewModal(false)}
            />
        )}
        </>
    );
}

export default memo(CourseRow); 
